import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
});

// Encoge la foto antes de subirla: de ~6 MB a ~300 KB sin perder legibilidad
async function reducirImagen(archivo: File): Promise<File> {
  if (archivo.type === 'application/pdf') return archivo;

  try {
    const bitmap = await createImageBitmap(archivo);
    const ANCHO_MAX = 1600;
    const escala = Math.min(1, ANCHO_MAX / bitmap.width);

    const lienzo = document.createElement('canvas');
    lienzo.width = Math.round(bitmap.width * escala);
    lienzo.height = Math.round(bitmap.height * escala);
    lienzo.getContext('2d')!.drawImage(bitmap, 0, 0, lienzo.width, lienzo.height);

    const blob = await new Promise<Blob | null>(r =>
      lienzo.toBlob(r, 'image/jpeg', 0.75));
    if (!blob) return archivo;

    return new File([blob], archivo.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
  } catch {
    return archivo;  // si algo falla, se sube la original
  }
}

// Añadir token automáticamente a cada petición
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
    config.headers['X-CRM-API'] = '1';
  return config;
});

// Si el token expira o pierde permisos, redirigir al login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // No redirigir si el 401 viene del propio inicio de sesión:
    // ahí el error lo gestiona la pantalla de login (credenciales, 2FA...).
    const esLogin = error.config?.url?.includes('/token');
    if (error.response?.status === 401 && !esLogin) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);
export default api;

// ── VERIFICACIÓN EN DOS PASOS ─────────────────────────────
export const estado2FA = () => api.get('/2fa/estado').then(r => r.data);
export const iniciar2FA = () => api.post('/2fa/iniciar').then(r => r.data);
export const confirmar2FA = (codigo: string) =>
  api.post('/2fa/confirmar', { codigo }).then(r => r.data);
export const desactivar2FA = (password: string) =>
  api.post('/2fa/desactivar', { password }).then(r => r.data);
export const resetear2FAUsuario = (usuarioId: number) =>
  api.put(`/usuarios/${usuarioId}/resetear-2fa`).then(r => r.data);

// ── AUTH ──────────────────────────────────────────────────
export const login = async (email: string, password: string, codigo2fa?: string) => {
  const formData = new FormData();
  formData.append('username', email);
  formData.append('password', password);
  if (codigo2fa) formData.append('client_secret', codigo2fa);
  const response = await api.post('/token', formData);
  return response.data;
};

// ── RECUPERACIÓN DE CONTRASEÑA (endpoints públicos, sin token) ──
export const solicitarRecuperacion = (email: string) =>
  api.post('/recuperar-password/solicitar', { email }).then(r => r.data);
export const confirmarRecuperacion = (email: string, codigo: string, passwordNueva: string) =>
  api.post('/recuperar-password/confirmar', { email, codigo, password_nueva: passwordNueva }).then(r => r.data);

// ── SUPERHOOK (asistente IA) ──────────────────────────────
export const preguntarSuperHook = (pregunta: string, historial: any[] = []) =>
  api.post('/superhook/preguntar', { pregunta, historial }).then(r => r.data);
export const ejecutarAccionSuperHook = (accion: any) =>
  api.post('/superhook/ejecutar-accion', accion).then(r => r.data);

// ── PERFIL ────────────────────────────────────────────────
export const cambiarPassword = async (datos: any) => {
  const response = await api.put('/perfil/password', datos);
  return response.data;
};

export const subirFoto = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/perfil/foto', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// ── FOTO DE PERFIL ────────────────────────────────────────
export const getFotoPerfilUrl = async (userId: number): Promise<string> => {
  try {
    const response = await api.get(`/perfil/foto/${userId}`, { responseType: 'blob' });
    return window.URL.createObjectURL(response.data);
  } catch {
    return '';
  }
};

// ── DOCUMENTACIÓN CONFIDENCIAL ────────────────────────────
export const getDocumentacion = (tecnicoId: number) =>
  api.get(`/documentacion/${tecnicoId}`).then(r => r.data);

export const subirDocumentacion = (tecnicoId: number, titulo: string, tipo: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/documentacion/${tecnicoId}?titulo=${encodeURIComponent(titulo)}&tipo=${encodeURIComponent(tipo)}`, formData)
    .then(r => r.data);
};

export const borrarDocumentacion = (docId: number) =>
  api.delete(`/documentacion/${docId}`).then(r => r.data);

export const descargarDocumentacion = async (docId: number, titulo: string) => {
  const response = await api.get(`/documentacion/archivo/${docId}`, { responseType: 'blob' });

  // Intentar obtener el nombre real del archivo desde la cabecera del backend
  let nombreArchivo = titulo || `documento_${docId}`;
  const disposition = response.headers['content-disposition'];
  if (disposition) {
    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
    if (match && match[1]) {
      nombreArchivo = decodeURIComponent(match[1]);
    }
  } else {
    // Si no hay cabecera, preservar la extensión según el tipo MIME del blob
    const tipo = response.data.type || '';
    if (tipo.includes('pdf') && !nombreArchivo.toLowerCase().endsWith('.pdf')) {
      nombreArchivo += '.pdf';
    }
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombreArchivo);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ── DASHBOARD TARJETAS ────────────────────────────────────
export const getDashboardTarjetas = () =>
  api.get('/dashboard/tarjetas').then(r => r.data);
export const getTarjetasDisponibles = () =>
  api.get('/dashboard/tarjetas/disponibles').then(r => r.data);
export const guardarTarjetas = (tarjetas: string[]) =>
  api.post('/dashboard/tarjetas', { tarjetas }).then(r => r.data);

// ── TABLÓN DE AVISOS ──────────────────────────────────────
export const getAvisos = () =>
  api.get('/avisos').then(r => r.data);
export const crearAviso = (datos: any) =>
  api.post('/avisos', datos).then(r => r.data);
export const actualizarAviso = (id: number, datos: any) =>
  api.put(`/avisos/${id}`, datos).then(r => r.data);
export const borrarAviso = (id: number) =>
  api.delete(`/avisos/${id}`).then(r => r.data);

// ── TAREAS (AGENDA) ───────────────────────────────────────
export const getTareas = (usuarioId?: number) =>
  api.get('/tareas', { params: usuarioId ? { usuario_id: usuarioId } : {} }).then(r => r.data);
export const crearTarea = (datos: any) =>
  api.post('/tareas', datos).then(r => r.data);
export const actualizarTarea = (id: number, datos: any) =>
  api.put(`/tareas/${id}`, datos).then(r => r.data);
export const completarTarea = (id: number) =>
  api.put(`/tareas/${id}/completar`).then(r => r.data);
export const borrarTarea = (id: number) =>
  api.delete(`/tareas/${id}`).then(r => r.data);
export const getMiEquipo = () =>
  api.get('/mi-equipo').then(r => r.data);

// ── AGENDA COMPARTIDA ─────────────────────────────────────
export const getAgendasVisibles = () =>
  api.get('/agenda/visibles').then(r => r.data);
export const compartirAgenda = (datos: any) =>
  api.post('/agenda/compartir', datos).then(r => r.data);
export const getMisAgendasCompartidas = () =>
  api.get('/agenda/mis-compartidas').then(r => r.data);
export const revocarAgendaCompartida = (id: number) =>
  api.delete(`/agenda/compartir/${id}`).then(r => r.data);
export const getUsuariosLista = () =>
  api.get('/usuarios/lista').then(r => r.data);

// ── RRHH ──────────────────────────────────────────────────
export const getDiasDisponibles = async () => {
  const response = await api.get('/perfil/dias-disponibles');
  return response.data;
};

export const getSolicitudes = async () => {
  const response = await api.get('/solicitudes');
  return response.data;
};

export const crearSolicitud = async (datos: any) => {
  const response = await api.post('/solicitudes', datos);
  return response.data;
};

export const resolverSolicitud = async (id: number, datos: any) => {
  const response = await api.put(`/solicitudes/${id}`, datos);
  return response.data;
};

export const getFichajeHoy = async () => {
  const response = await api.get('/fichajes/hoy');
  return response.data;
};

export const ficharEntrada = async () => {
  const response = await api.post('/fichajes/entrada');
  return response.data;
};

export const ficharPausaInicio = async () => {
  const response = await api.post('/fichajes/pausa-inicio');
  return response.data;
};

export const ficharPausaFin = async () => {
  const response = await api.post('/fichajes/pausa-fin');
  return response.data;
};

export const ficharSalida = async () => {
  const response = await api.post('/fichajes/salida');
  return response.data;
};

export const getHistorialFichajes = async () => {
  const response = await api.get('/fichajes/historial');
  return response.data;
};

export const actualizarDatosLaborales = async (id: number, datos: any) => {
  const response = await api.put(`/usuarios/${id}/datos-laborales`, datos);
  return response.data;
};

// ── DASHBOARD ─────────────────────────────────────────────
export const getDashboard = async () => {
  const response = await api.get('/dashboard');
  return response.data;
};

// ── CLIENTES ──────────────────────────────────────────────

export const getFichaCliente = (id: number) =>
  api.get(`/clientes/${id}/ficha`).then(r => r.data);
export const asignarClienteMaquina = (maquinaId: number, clienteId: number | null) =>
  api.put(`/maquinas/${maquinaId}/asignar-cliente`, { cliente_id: clienteId }).then(r => r.data);

export const getClientes = async (search = '', estado = '') => {
  const response = await api.get('/clientes', { params: { search, estado } });
  return response.data;
};

export const getCliente = async (id: number) => {
  const response = await api.get(`/clientes/${id}`);
  return response.data;
};

export const crearCliente = async (datos: any) => {
  const response = await api.post('/clientes', datos);
  return response.data;
};

export const actualizarCliente = async (id: number, datos: any) => {
  const response = await api.put(`/clientes/${id}`, datos);
  return response.data;
};

export const archivarCliente = async (id: number, estado: string) => {
  const response = await api.put(`/clientes/${id}/archivar`, { estado });
  return response.data;
};

export const borrarCliente = async (id: number) => {
  const response = await api.delete(`/clientes/${id}`);
  return response.data;
};

export const getComerciales = async () => {
  const response = await api.get('/comerciales');
  return response.data;
};

// ── TARIFARIO Y TIEMPOS ───────────────────────────────────
export const getTarifario = () => api.get('/tarifario').then(r => r.data);
export const actualizarTarifa = (clave: string, datos: any) =>
  api.put(`/tarifario/${clave}`, datos).then(r => r.data);
export const calcularTiempo = (datos: any) =>
  api.post('/intervenciones/calcular-tiempo', datos).then(r => r.data);


// ── INTERVENCIONES ────────────────────────────────────────
export const getIntervenciones = async () => {
  const response = await api.get('/intervenciones');
  return response.data;
};
export const previsualizarBorradoIntervencion = (id: number) =>
  api.get(`/intervenciones/v2/${id}/previsualizar-borrado`).then(r => r.data);

export const borrarIntervencion = (id: number) =>
  api.delete(`/intervenciones/v2/${id}`).then(r => r.data);

export const eliminarMaterial = (materialId: number) =>
  api.delete(`/intervenciones/materiales/${materialId}`).then(r => r.data);

// ── MÓDULO COMERCIAL ──────────────────────────────────────
export const getOportunidadesComerciales = () =>
  api.get('/comercial/oportunidades').then(r => r.data);
export const crearOportunidadComercial = (datos: any) =>
  api.post('/comercial/oportunidades', datos).then(r => r.data);
export const actualizarOportunidadComercial = (id: number, datos: any) =>
  api.put(`/comercial/oportunidades/${id}`, datos).then(r => r.data);
export const getMotivosCierre = () =>
  api.get('/comercial/motivos-cierre').then(r => r.data);
export const cerrarOportunidad = (id: number, datos: any) =>
  api.put(`/comercial/oportunidades/${id}/cerrar`, datos).then(r => r.data);
export const reabrirOportunidad = (id: number, fase: string = 'negociacion') =>
  api.put(`/comercial/oportunidades/${id}/reabrir`, { fase }).then(r => r.data);
export const borrarOportunidadComercial = (id: number) =>
  api.delete(`/comercial/oportunidades/${id}`).then(r => r.data);

export const getVisitasComerciales = () =>
  api.get('/comercial/visitas').then(r => r.data);
export const crearVisitaComercial = (datos: any) =>
  api.post('/comercial/visitas', datos).then(r => r.data);
export const actualizarVisitaComercial = (id: number, datos: any) =>
  api.put(`/comercial/visitas/${id}`, datos).then(r => r.data);
export const borrarVisitaComercial = (id: number) =>
  api.delete(`/comercial/visitas/${id}`).then(r => r.data);

// ── ESTUDIOS DE OPERACIÓN ─────────────────────────────────
export const getEstudios = (plantillas: boolean = false) =>
  api.get('/estudios-operacion', { params: { plantillas: plantillas ? 1 : 0 } }).then(r => r.data);
export const getEstudio = (id: number) =>
  api.get(`/estudios-operacion/${id}`).then(r => r.data);
export const crearEstudio = (datos: any) =>
  api.post('/estudios-operacion', datos).then(r => r.data);
export const actualizarEstudio = (id: number, datos: any) =>
  api.put(`/estudios-operacion/${id}`, datos).then(r => r.data);
export const borrarEstudio = (id: number) =>
  api.delete(`/estudios-operacion/${id}`).then(r => r.data);

// ── MÁQUINAS ──────────────────────────────────────────────
export const getMaquinas = async () => {
  const response = await api.get('/maquinas');
  return response.data;
};
export const getDocumentoMaquinaBlob = (docId: number) =>
  api.get(`/documentos_maquina/${docId}/archivo`, { responseType: 'blob' }).then(r => r.data);

export const borrarDocumentoMaquina = (docId: number) =>
  api.delete(`/documentos_maquina/${docId}`).then(r => r.data);

export const getTelemetriaMaquina = (maquinaId: number) =>
  api.get(`/maquinas/${maquinaId}/telemetria`).then(r => r.data);

export const sincronizarMachineLink = () =>
  api.post('/machinelink/importar').then(r => r.data);

export const crearMaquina = async (datos: any) => {
  const response = await api.post('/maquinas', datos);
  return response.data;
};

export const getMaquinaDetalle = async (id: number) => {
  const response = await api.get(`/maquinas/${id}`);
  return response.data;
};

export const getDivisionesMaquinas = () =>
  api.get('/divisiones-maquinas').then(r => r.data);
export const subirDocumentoMaquina = async (maquinaId: number, titulo: string, tipo: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/maquinas/${maquinaId}/documentos?titulo=${encodeURIComponent(titulo)}&tipo=${tipo}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const actualizarMaquina = async (id: number, datos: any) => {
  const response = await api.put(`/maquinas/${id}`, datos);
  return response.data;
};

// ── COST CONTROL DE MÁQUINAS ──────────────────────────────
export const getCostControl = (maquinaId: number) =>
  api.get(`/maquinas/${maquinaId}/cost-control`).then(r => r.data);
export const crearMovimientoCC = (maquinaId: number, datos: any) =>
  api.post(`/maquinas/${maquinaId}/cost-control`, datos).then(r => r.data);
export const borrarMovimientoCC = (movId: number) =>
  api.delete(`/cost-control/${movId}`).then(r => r.data);

// ── COST CONTROL DE CLIENTES ──────────────────────────────
export const getCostControlCliente = (clienteId: number) =>
  api.get(`/clientes/${clienteId}/cost-control`).then(r => r.data);
export const crearMovimientoCCCliente = (clienteId: number, datos: any) =>
  api.post(`/clientes/${clienteId}/cost-control`, datos).then(r => r.data);


// ── RECAMBIOS ─────────────────────────────────────────────
export const getRecambios = async (search = '', page = 1) => {
  const response = await api.get('/recambios', { params: { search, page } });
  return response.data;
  
};
export const exportarRecambios = () =>
  api.get('/recambios/exportar').then(r => r.data);
// ── USUARIOS ──────────────────────────────────────────────
export const getUsuarios = async () => {
  const response = await api.get('/usuarios');
  return response.data;
};

export const crearUsuario = async (datos: any) => {
  const response = await api.post('/usuarios', datos);
  return response.data;
};

export const actualizarUsuario = async (id: number, datos: any) => {
  const response = await api.put(`/usuarios/${id}`, datos);
  return response.data;
};

export const getOrganigrama = () =>
  api.get('/organigrama').then(r => r.data);

export const asignarResponsable = (userId: number, responsableId: number | null) =>
  api.put(`/usuarios/${userId}/responsable`, { responsable_id: responsableId }).then(r => r.data);

export const tengoEquipo = () =>
  api.get('/tengo-equipo').then(r => r.data.tiene_equipo);

export const getFichajesEquipo = () =>
  api.get('/fichajes/equipo').then(r => r.data);

export const getCostesUsuario = (id: number) =>
  api.get(`/costes-personal/${id}`).then(r => r.data);
export const actualizarCostesUsuario = (id: number, costes: Record<string, number>) =>
  api.put(`/costes-personal/${id}`, { costes }).then(r => r.data);


// ── ALMACÉN ───────────────────────────────────────────────
export const getAlertasStock = async () => {
  const response = await api.get('/recambios/alertas');
  return response.data;
};

export const crearRecambio = async (datos: any) => {
  const response = await api.post('/recambios', datos);
  return response.data;
};

export const getMovimientos = async () => {
  const response = await api.get('/movimientos');
  return response.data;
};

export const crearMovimiento = async (datos: any) => {
  const response = await api.post('/movimientos', datos);
  return response.data;
};

export const getProveedores = async () => {
  const response = await api.get('/proveedores');
  return response.data;
};

export const crearProveedor = async (datos: any) => {
  const response = await api.post('/proveedores', datos);
  return response.data;
};

export const getMaquinariaStock = async () => {
  const response = await api.get('/maquinaria_stock');
  return response.data;
};
// ── OFERTAS ───────────────────────────────────────────────
export const crearOferta = async (datos: any) => {
  const response = await api.post('/ofertas', datos);
  return response.data;
};

export const confirmarVenta = async (ofertaId: number, datos: any) => {
  const response = await api.post(`/ofertas/${ofertaId}/confirmar`, datos);
  return response.data;
};
export const getOfertas = async () => {
  const response = await api.get('/ofertas');
  return response.data;
};

export const getLineasOferta = async (ofertaId: number) => {
  const response = await api.get(`/ofertas/${ofertaId}/lineas`);
  return response.data;
};

export const descargarAlbaran = async (ofertaId: number) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`https://${API_URL}:8000/ofertas/${ofertaId}/albaran`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.blob();
};

export const buscarRecambioPorReferencia = async (referencia: string) => {
  const response = await api.get(`/recambios/buscar/${referencia}`);
  return response.data;
};
// ── VENTAS PENDIENTES DE FACTURAR ──────────────────────────
export const getVentasPendientesFacturar = async () => {
  const response = await api.get('/ventas-pendientes-facturar');
  return response.data;
};

export const marcarFacturado = async (ventaId: number) => {
  const response = await api.put(`/ventas-pendientes-facturar/${ventaId}/marcar-facturado`);
  return response.data;
};

export const getLineasVenta = async (ventaId: number) => {
  const response = await api.get(`/ventas-pendientes-facturar/${ventaId}/lineas`);
  return response.data;
};
// ── CONFIGURACIÓN NOTIFICACIONES ───────────────────────────
export const getConfigNotificaciones = async () => {
  const response = await api.get('/config-notificaciones');
  return response.data;
};

export const activarNotificacion = async (tecnicoId: number, tipo: string) => {
  const response = await api.post('/config-notificaciones', { tecnico_id: tecnicoId, tipo_notificacion: tipo });
  return response.data;
};

export const desactivarNotificacion = async (tecnicoId: number, tipo: string) => {
  const response = await api.delete(`/config-notificaciones?tecnico_id=${tecnicoId}&tipo_notificacion=${tipo}`);
  return response.data;
};

export const analizarAlbaran = async (file: File) => {
  const formData = new FormData();
  const archivoReducido = await reducirImagen(file);
  formData.append('file', archivoReducido);
  const response = await api.post('/megascan/analizar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// ── INTERVENCIONES V2 ───────────────────────────────────────
export const getTiposIntervencion = async () => {
  const response = await api.get('/intervenciones/tipos');
  return response.data;
};

export const getIntervencionesV2 = async (params: any = {}) => {
  const response = await api.get('/intervenciones/v2', { params });
  return response.data;
};

export const crearIntervencionV2 = async (datos: any) => {
  const response = await api.post('/intervenciones/v2', datos);
  return response.data;
};

export const getIntervencionDetalle = async (id: number) => {
  const response = await api.get(`/intervenciones/v2/${id}`);
  return response.data;
};

export const añadirTiempo = async (intervencionId: number, datos: any) => {
  const response = await api.post(`/intervenciones/v2/${intervencionId}/tiempos`, datos);
  return response.data;
};

export const editarTiempo = (intervencionId: number, tiempoId: number, datos: any) =>
  api.put(`/intervenciones/v2/${intervencionId}/tiempos/${tiempoId}`, datos).then(r => r.data);


export const solicitarMaterial = async (intervencionId: number, datos: any) => {
  const response = await api.post(`/intervenciones/v2/${intervencionId}/materiales`, datos);
  return response.data;
};

export const getMaterialesPendientes = async () => {
  const response = await api.get('/intervenciones/materiales/pendientes');
  return response.data;
};

export const aprobarMaterial = async (materialId: number) => {
  const response = await api.put(`/intervenciones/materiales/${materialId}/aprobar`);
  return response.data;
};

export const guardarInforme = async (intervencionId: number, informeTexto: string, enviar: boolean = false) => {
  const response = await api.put(`/intervenciones/v2/${intervencionId}/informe`,
    { informe_texto: informeTexto, enviar });
  return response.data;
};

export const aprobarIntervencion = async (intervencionId: number, datos: any = {}) => {
  const response = await api.put(`/intervenciones/v2/${intervencionId}/aprobar`, datos);
  return response.data;
};

export const rechazarIntervencion = async (intervencionId: number, nota: string) => {
  const response = await api.put(`/intervenciones/v2/${intervencionId}/rechazar`, { nota });
  return response.data;
};

export const descargarInformeIntervencion = async (id: number) => {
  const response = await api.get(`/intervenciones/v2/${id}/informe-pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Informe_INT-${id}.pdf`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const guardarFirmaIntervencion = (id: number, tipo: 'tecnico' | 'cliente', imagen: string) =>
  api.put(`/intervenciones/v2/${id}/firma`, { tipo, imagen }).then(r => r.data);
export const enviarInformeEmail = (id: number, email: string) =>
  api.post(`/intervenciones/v2/${id}/enviar-informe`, { email }).then(r => r.data);
export const getFotosIntervencion = (id: number) =>
  api.get(`/intervenciones/v2/${id}/fotos`).then(r => r.data);
export const subirFotoIntervencion = (id: number, file: File, titulo: string = '') => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/intervenciones/v2/${id}/fotos?titulo=${encodeURIComponent(titulo)}`, form).then(r => r.data);
};
export const borrarFotoIntervencion = (fotoId: number) =>
  api.delete(`/intervenciones/fotos/${fotoId}`).then(r => r.data);
export const getFotoIntervencionBlob = async (fotoId: number) => {
  const response = await api.get(`/intervenciones/fotos/${fotoId}/archivo`, { responseType: 'blob' });
  return window.URL.createObjectURL(response.data);
};
export const actualizarTecnicosIntervencion = async (id: number, tecnicosIds: number[]) => {
  const response = await api.put(`/intervenciones/v2/${id}/tecnicos`, { tecnicos_ids: tecnicosIds });
  return response.data;
}
export const actualizarDatosIntervencion = async (id: number, datos: any) => {
  const response = await api.put(`/intervenciones/v2/${id}/datos`, datos);
  return response.data;
};
export const rechazarMaterial = async (materialId: number) => {
  const response = await api.put(`/intervenciones/materiales/${materialId}/rechazar`);
  return response.data;
};
export const getControlEconomico = async (id: number) => {
  const response = await api.get(`/intervenciones/v2/${id}/economico`);
  return response.data;
};
export const actualizarTarifas = async (id: number, datos: any) => {
  const response = await api.put(`/intervenciones/v2/${id}/tarifas`, datos);
  return response.data;
};
export const generarInformeIA = (intervencionId: number, notas: string,
                                 informeActual: string = '', fecha?: string) =>
  api.post(`/intervenciones/v2/${intervencionId}/generar-informe`,
    { notas, informe_actual: informeActual, fecha }).then(r => r.data);

// ── HEI ACADEMY ───────────────────────────────────────────
export const getCursosAcademy = async () => {
  const response = await api.get('/academy/cursos');
  return response.data;
};

export const crearCursoAcademy = async (datos: any) => {
  const response = await api.post('/academy/cursos', datos);
  return response.data;
};

export const getLeccionesAcademy = async (cursoId: number) => {
  const response = await api.get(`/academy/cursos/${cursoId}/lecciones`);
  return response.data;
};

export const getAcademyUsuarios = async () => {
  const response = await api.get('/academy/usuarios');
  return response.data;
};

export const crearAcademyUsuario = async (datos: any) => {
  const response = await api.post('/academy/usuarios', datos);
  return response.data;
};

export const asignarCurso = async (cursoId: number, datos: any) => {
  const response = await api.post(`/academy/cursos/${cursoId}/asignar`, datos);
  return response.data;
};

export const getAsignacionesCurso = async (cursoId: number) => {
  const response = await api.get(`/academy/cursos/${cursoId}/asignaciones`);
  return response.data;
};

export const getMisCursosEmpleado = async (tecnicoId: number) => {
  const response = await api.get(`/academy/mis-cursos-empleado?tecnico_id=${tecnicoId}`);
  return response.data;
};

// ── PERMISOS ──────────────────────────────────────────────
export const getPermisosUsuario = async (userId: number) => {
  const response = await api.get(`/permisos/${userId}`);
  return response.data;
};

export const setPermiso = async (userId: number, permiso: string) => {
  const response = await api.post(`/permisos/${userId}`, { permiso });
  return response.data;
};

export const quitarPermiso = async (userId: number, permiso: string) => {
  const response = await api.delete(`/permisos/${userId}?permiso=${permiso}`);
  return response.data;
};

export const getMisPermisos = async () => {
  const response = await api.get('/me/permisos');
  return response.data;
};

// ── ERP: FACTURACIÓN ──────────────────────────────────────
export const getConfigERP = () => api.get('/erp/config').then(r => r.data);
export const actualizarEmpresa = (datos: any) => api.put('/erp/empresa', datos).then(r => r.data);
export const getFacturas = (params: any = {}) =>
  api.get('/facturas', { params }).then(r => r.data);
export const getFactura = (id: number) => api.get(`/facturas/${id}`).then(r => r.data);
export const crearFactura = (datos: any) => api.post('/facturas', datos).then(r => r.data);
export const actualizarFactura = (id: number, datos: any) =>
  api.put(`/facturas/${id}`, datos).then(r => r.data);
export const anularFactura = (id: number, motivo: string) =>
  api.put(`/facturas/${id}/anular`, { motivo }).then(r => r.data);
export const crearSerie = (datos: any) => api.post('/erp/series', datos).then(r => r.data);
export const actualizarFormaPago = (clave: string, datos: any) =>
  api.put(`/erp/formas-pago/${clave}`, datos).then(r => r.data);
export const crearFormaPago = (datos: any) => api.post('/erp/formas-pago', datos).then(r => r.data);

// ── ERP: TESORERÍA ────────────────────────────────────────
export const getVencimientos = (params: any = {}) =>
  api.get('/tesoreria/vencimientos', { params }).then(r => r.data);
export const getPrevision = () => api.get('/tesoreria/prevision').then(r => r.data);
export const liquidarVencimiento = (id: number, datos: any) =>
  api.post(`/tesoreria/vencimientos/${id}/liquidar`, datos).then(r => r.data);
export const crearVencimiento = (datos: any) =>
  api.post('/tesoreria/vencimientos', datos).then(r => r.data);
export const descargarFacturaPDF = async (id: number, numero: string) => {
  const response = await api.get(`/facturas/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Factura_${numero.replace('/', '-')}.pdf`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const facturarVentaPendiente = (ventaId: number, datos: any = {}) =>
  api.post(`/ventas-pendientes-facturar/${ventaId}/facturar`, datos).then(r => r.data);
// ── ALMACÉN v2 ────────────────────────────────────────────
export const getRecambiosDivision = (search: string, page: number, division: string) =>
  api.get('/recambios', { params: { search, page, division } }).then(r => r.data);
export const getDivisionesStock = () => api.get('/recambios-divisiones').then(r => r.data);
export const actualizarRecambio = (id: number, datos: any) =>
  api.put(`/recambios/${id}`, datos).then(r => r.data);
export const aceptarOferta = (id: number) =>
  api.post(`/ofertas/${id}/aceptar`).then(r => r.data);
export const actualizarOferta = (id: number, datos: any) =>
  api.put(`/ofertas/${id}`, datos).then(r => r.data);
export const borrarOferta = (id: number) =>
  api.delete(`/ofertas/${id}`).then(r => r.data);
// ── PEDIDOS DE CLIENTE ────────────────────────────────────
export const getPedidosCliente = (params: any = {}) =>
  api.get('/pedidos-cliente', { params }).then(r => r.data);
export const getPedidoCliente = (id: number) =>
  api.get(`/pedidos-cliente/${id}`).then(r => r.data);
export const cambiarEstadoPedido = (id: number, datos: any) =>
  api.put(`/pedidos-cliente/${id}/estado`, datos).then(r => r.data);
// ── COMPRAS ───────────────────────────────────────────────
export const getPedidosCompra = (params: any = {}) =>
  api.get('/compras/pedidos', { params }).then(r => r.data);
export const getPedidoCompra = (id: number) =>
  api.get(`/compras/pedidos/${id}`).then(r => r.data);
export const crearPedidoCompra = (datos: any) =>
  api.post('/compras/pedidos', datos).then(r => r.data);
export const actualizarPedidoCompra = (id: number, datos: any) =>
  api.put(`/compras/pedidos/${id}`, datos).then(r => r.data);
export const cambiarEstadoPedidoCompra = (id: number, estado: string) =>
  api.put(`/compras/pedidos/${id}/estado`, { estado }).then(r => r.data);
export const registrarRecepcion = (id: number, datos: any) =>
  api.post(`/compras/pedidos/${id}/recepciones`, datos).then(r => r.data);
export const actualizarProveedor = (id: number, datos: any) =>
  api.put(`/proveedores/${id}`, datos).then(r => r.data);
export const getCategoriasCompra = () => api.get('/compras/categorias').then(r => r.data);
export const getProveedoresCompras = (params: any = {}) =>
  api.get('/compras/proveedores', { params }).then(r => r.data);
export const crearProveedorCompras = (datos: any) =>
  api.post('/compras/proveedores', datos).then(r => r.data);
export const getFacturasProveedor = (params: any = {}) =>
  api.get('/compras/facturas', { params }).then(r => r.data);
export const registrarFacturaProveedor = (id: number, datos: any) =>
  api.put(`/compras/facturas/${id}/registrar`, datos).then(r => r.data);
export const crearFacturaProveedor = (datos: any) =>
  api.post('/compras/facturas', datos).then(r => r.data);
export const subirArchivoFacturaProveedor = (id: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/compras/facturas/${id}/archivo`, form).then(r => r.data);
};
export const descargarArchivoFacturaProveedor = async (id: number, nombre: string) => {
  const response = await api.get(`/compras/facturas/${id}/archivo`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre || 'factura';
  link.click();
  window.URL.revokeObjectURL(url);
};
export const getFacturasCobrar = (params: any = {}) =>
  api.get('/facturas-cobrar', { params }).then(r => r.data);
export const eliminarUsuario = (id: number) =>
  api.delete(`/usuarios/${id}`).then(r => r.data);
// ── GASTOS DE INTERVENCIÓN ────────────────────────────────
export const getTiposGasto = () => api.get('/intervenciones/gastos/tipos').then(r => r.data);
export const getGastosIntervencion = (id: number) =>
  api.get(`/intervenciones/v2/${id}/gastos`).then(r => r.data);
export const crearGastoIntervencion = (id: number, datos: any, file?: File | null) => {
  const form = new FormData();
  Object.entries(datos).forEach(([k, v]) => form.append(k, v === null || v === undefined ? '' : String(v)));
  if (file) form.append('file', file);
  return api.post(`/intervenciones/v2/${id}/gastos`, form).then(r => r.data);
};
export const validarGasto = (gastoId: number) =>
  api.put(`/intervenciones/gastos/${gastoId}/validar`, {}).then(r => r.data);
export const rechazarGasto = (gastoId: number, motivo: string) =>
  api.put(`/intervenciones/gastos/${gastoId}/rechazar`, { motivo }).then(r => r.data);
export const borrarGastoIntervencion = (gastoId: number) =>
  api.delete(`/intervenciones/gastos/${gastoId}`).then(r => r.data);
export const descargarTicketGasto = async (gastoId: number, nombre: string) => {
  const response = await api.get(`/intervenciones/gastos/${gastoId}/archivo`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre || 'ticket';
  link.click();
  window.URL.revokeObjectURL(url);
};
// ── INCIDENCIAS DEL SIS ───────────────────────────────────
export const crearIncidencia = (datos: any, file?: File | null) => {
  const form = new FormData();
  Object.entries(datos).forEach(([k, v]) => form.append(k, v === null || v === undefined ? '' : String(v)));
  if (file) form.append('file', file);
  return api.post('/incidencias', form).then(r => r.data);
};
export const getIncidencias = (params: any = {}) =>
  api.get('/incidencias', { params }).then(r => r.data);
export const resolverIncidencia = (id: number, datos: any) =>
  api.put(`/incidencias/${id}`, datos).then(r => r.data);
export const descargarAdjuntoIncidencia = async (id: number, nombre: string) => {
  const response = await api.get(`/incidencias/${id}/archivo`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre || 'adjunto';
  link.click();
  window.URL.revokeObjectURL(url);
};
export const getPropuestaFactura = (intervencionId: number) =>
  api.get(`/intervenciones/v2/${intervencionId}/propuesta-factura`).then(r => r.data);
export const facturarIntervencion = (intervencionId: number, datos: any) =>
  api.post(`/intervenciones/${intervencionId}/facturar`, datos).then(r => r.data);

// ── PROFORMAS ─────────────────────────────────────────────
export const getProformas = (params: any = {}) =>
  api.get('/proformas', { params }).then(r => r.data);
export const getProforma = (id: number) => api.get(`/proformas/${id}`).then(r => r.data);
export const crearProforma = (datos: any) => api.post('/proformas', datos).then(r => r.data);
export const actualizarProforma = (id: number, datos: any) =>
  api.put(`/proformas/${id}`, datos).then(r => r.data);
export const anularProforma = (id: number) =>
  api.put(`/proformas/${id}/anular`, {}).then(r => r.data);
export const convertirProformaEnFactura = (id: number, datos: any = {}) =>
  api.post(`/proformas/${id}/facturar`, datos).then(r => r.data);
export const descargarProformaPDF = async (id: number, numero: string) => {
  const response = await api.get(`/proformas/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Proforma_${numero.replace('/', '-')}.pdf`;
  link.click();
  window.URL.revokeObjectURL(url);
};
export const proformaDesdeVenta = (ventaId: number, datos: any = {}) =>
  api.post(`/ventas-pendientes-facturar/${ventaId}/proforma`, datos).then(r => r.data);

// ── OFERTAS COMERCIALES ───────────────────────────────────
export const getOfertasComerciales = (params: any = {}) =>
  api.get('/ofertas-comerciales', { params }).then(r => r.data);
export const getOfertaComercial = (id: number) =>
  api.get(`/ofertas-comerciales/${id}`).then(r => r.data);
export const crearOfertaComercial = (datos: any) =>
  api.post('/ofertas-comerciales', datos).then(r => r.data);
export const actualizarOfertaComercial = (id: number, datos: any) =>
  api.put(`/ofertas-comerciales/${id}`, datos).then(r => r.data);
export const cambiarEstadoOfertaComercial = (id: number, estado: string, motivo?: string) =>
  api.put(`/ofertas-comerciales/${id}/estado`, { estado, motivo }).then(r => r.data);
export const duplicarOfertaComercial = (id: number, datos: any = {}) =>
  api.post(`/ofertas-comerciales/${id}/duplicar`, datos).then(r => r.data);
export const borrarOfertaComercial = (id: number) =>
  api.delete(`/ofertas-comerciales/${id}`).then(r => r.data);
export const proformaDesdeOfertaComercial = (id: number) =>
  api.post(`/ofertas-comerciales/${id}/proforma`, {}).then(r => r.data);
export const facturarOfertaComercial = (id: number, datos: any = {}) =>
  api.post(`/ofertas-comerciales/${id}/facturar`, datos).then(r => r.data);
export const descargarOfertaComercialPDF = async (id: number, numero: string) => {
  const response = await api.get(`/ofertas-comerciales/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Oferta_${(numero || id).toString().replace('/', '-')}.pdf`;
  link.click();
  window.URL.revokeObjectURL(url);
};
export const reenviarAcceso = (id: number) =>
  api.post(`/usuarios/${id}/reenviar-acceso`, {}).then(r => r.data);
export const descargarAlbaranPedido = async (pedidoId: number, numero: string, valorado = false) => {
  const response = await api.get(`/pedidos-cliente/${pedidoId}/albaran`,
    { params: { valorado: valorado ? 1 : 0 }, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Albaran_${(numero || pedidoId).toString().replace('/', '-')}${valorado ? '_valorado' : ''}.pdf`;
  link.click();
  window.URL.revokeObjectURL(url);
};
export const getDiasSinFichar = () =>
  api.get('/fichajes/dias-sin-fichar').then(r => r.data);
export const ficharDiaOlvidado = (datos: any) =>
  api.post('/fichajes/dia-olvidado', datos).then(r => r.data);
export const descargarPedidoCompraPDF = async (id: number, numero: string) => {
  const response = await api.get(`/compras/pedidos/${id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `Pedido_${(numero || id).toString().replace('/', '-')}.pdf`;
  link.click();
  window.URL.revokeObjectURL(url);
};
// ── VALORACIÓN DE INTERVENCIONES ──────────────────────────
export const getValoracion = (intervencionId: number) =>
  api.get(`/intervenciones/v2/${intervencionId}/valoracion`).then(r => r.data);

export const guardarValoracion = (intervencionId: number, lineas: any[]) =>
  api.put(`/intervenciones/v2/${intervencionId}/valoracion`, { lineas }).then(r => r.data);

export const confirmarValoracion = (intervencionId: number, lineas: any[]) =>
  api.post(`/intervenciones/v2/${intervencionId}/valoracion/confirmar`, { lineas }).then(r => r.data);
export const actualizarEntregaPedido = (pedidoId: number, datos: any) =>
  api.put(`/pedidos-cliente/${pedidoId}/entrega`, datos).then(r => r.data);
export const exportarClientesExcel = () =>
  api.get('/exportar/clientes').then(r => r.data);

export const exportarMaquinasExcel = (soloConAverias = 0) =>
  api.get('/exportar/maquinas', { params: { solo_con_averias: soloConAverias } }).then(r => r.data);